// A recipient's own page from the outreach (D-108): brandwall.app/for/<token>. It teases what
// Brandwall found about their brand, plays the pitch video, and gives the full report (Excel or
// PDF) for an email address and a yes. Opens, the form and downloads are counted by the API,
// only once the link was sent: while it's reviewed, nothing is kept.
import { type FormEvent, useEffect, useState } from "react";
import { useParams } from "react-router";
import { ApiError, api, compact, post } from "../api";

interface LandingData {
  live: boolean;
  brand: { name: string; website: string | null; logoUrl: string | null; colors: string[] };
  numbers: {
    customerPosts: number;
    creators: number | null;
    likes: number | null;
    hashtags: string[];
    prospects: number;
    competitors: string[];
  };
  pictures: { src: string; alt: string }[];
  video: string | null;
  consent: string;
}

interface Downloads {
  downloads: { xlsx: string; pdf: string };
  wallLink: string | null;
  reportLink: string | null;
}

const n = (v: number | null | undefined) => (v == null ? "–" : compact(Math.round(v)));
const list = (items: string[]) =>
  items.length < 2 ? (items[0] ?? "") : `${items.slice(0, -1).join(", ")} and ${items.at(-1)}`;

export function Landing() {
  const { token } = useParams();
  const [d, setD] = useState<LandingData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [yes, setYes] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState<Downloads | null>(null);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.append(meta);
    api<LandingData>(`/v1/public/outreach/${token}`)
      .then((r) => {
        setD(r);
        document.title = `What customers post about ${r.brand.name} · Brandwall`;
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : "This page isn't available."));
    return () => meta.remove();
  }, [token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setBusy(true);
    try {
      setDone(
        await post<Downloads>(`/v1/public/outreach/${token}/lead`, {
          email,
          ...(name.trim() ? { name } : {}),
          consent: true,
        }),
      );
    } catch (err) {
      setFormError(
        err instanceof ApiError && err.code !== "invalid"
          ? err.message
          : "Check your email address and the box, then try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="onb">{error}</div>;
  if (!d) return <div className="onb muted">…</div>;
  const b = d.brand;
  const x = d.numbers;
  const accent = b.colors[0] ?? "#10302a";
  const tags = x.hashtags.map((t) => `#${t}`);
  return (
    <div className="rp lp" style={{ ["--accent" as string]: accent }}>
      {!d.live && (
        <div className="demo-flag">Preview: nothing on this page is counted or kept yet.</div>
      )}
      <div className="rp-page">
        <header className="rp-head">
          <div>
            <span className="rp-eyebrow">For {b.name}</span>
            <h1>
              {x.customerPosts > 0
                ? `${n(x.customerPosts)} customer posts about ${b.name}`
                : `What customers post about ${b.name}`}
            </h1>
            <p className="muted">
              We looked at public Instagram posts{tags.length ? ` with ${list(tags)}` : ""} and
              found real people showing {b.name} in their own lives. Here's a first look. The full
              report is yours below.
            </p>
          </div>
          {b.logoUrl && <img src={b.logoUrl} alt={b.name} className="rp-logo" />}
        </header>

        {d.pictures.length > 0 && (
          <div className="lp-pics">
            {d.pictures.map((p) => (
              <img key={p.src} src={p.src} alt={p.alt} loading="lazy" />
            ))}
          </div>
        )}

        {d.video && (
          <section className="rp-section">
            <h2>A short film we made for {b.name}</h2>
            {/* biome-ignore lint/a11y/useMediaCaption: a narrated pitch without a captions file */}
            <video className="rp-shot" src={d.video} controls playsInline preload="metadata" />
          </section>
        )}

        <section className="rp-kpis">
          <div className="rp-kpi">
            <span>Customer posts</span>
            <b>{n(x.customerPosts)}</b>
          </div>
          {x.creators != null && (
            <div className="rp-kpi">
              <span>Different accounts</span>
              <b>{n(x.creators)}</b>
            </div>
          )}
          {x.likes != null && (
            <div className="rp-kpi">
              <span>Likes on them</span>
              <b>{n(x.likes)}</b>
            </div>
          )}
          {x.prospects > 0 && (
            <div className="rp-kpi">
              <span>Creators picked for you</span>
              <b>{n(x.prospects)}</b>
            </div>
          )}
        </section>

        <section className="rp-section">
          <h2>In the full report</h2>
          <ul className="rp-list">
            <li>
              <b>Every customer post we found</b>, the most liked first, with links.
            </li>
            {x.prospects > 0 && (
              <li>
                <b>{n(x.prospects)} creators who already post about you</b>, with their followers,
                engagement and why they fit {b.name}.
              </li>
            )}
            {x.competitors.length > 0 && (
              <li>
                <b>How you compare</b> with {list(x.competitors)}.
              </li>
            )}
            <li>
              <b>A private demo wall</b> with your customers' posts, as it could look on your site.
            </li>
          </ul>
        </section>

        <section className="rp-cta lp-form">
          {done ? (
            <div className="stack">
              <h2>Your report</h2>
              <div className="row wrap">
                <a className="btn" href={done.downloads.xlsx} download>
                  Download Excel
                </a>
                <a className="btn" href={done.downloads.pdf} download>
                  Download PDF
                </a>
                {done.reportLink && (
                  <a className="btn secondary" href={done.reportLink}>
                    Read it online
                  </a>
                )}
                {done.wallLink && (
                  <a className="btn secondary" href={done.wallLink}>
                    Open the demo wall
                  </a>
                )}
              </div>
            </div>
          ) : (
            <form className="stack" onSubmit={submit}>
              <div>
                <h2>Get the full report</h2>
                <p>Excel and PDF, free. We'll send nothing you didn't ask for.</p>
              </div>
              <div className="lp-fields">
                <label className="field" htmlFor="lp-email">
                  Work email
                  <input
                    id="lp-email"
                    className="input"
                    type="email"
                    autoComplete="email"
                    required
                    maxLength={254}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </label>
                <label className="field" htmlFor="lp-name">
                  Name (optional)
                  <input
                    id="lp-name"
                    className="input"
                    autoComplete="name"
                    maxLength={120}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </label>
              </div>
              <label className="lp-consent" htmlFor="lp-consent">
                <input
                  id="lp-consent"
                  type="checkbox"
                  required
                  checked={yes}
                  onChange={(e) => setYes(e.target.checked)}
                />
                <span>
                  {d.consent}{" "}
                  <a href="/privacy" target="_blank" rel="noreferrer">
                    Privacy
                  </a>
                </span>
              </label>
              {formError && <div className="error">{formError}</div>}
              <button className="btn" type="submit" disabled={busy || !yes || !email}>
                {busy ? "One moment…" : "Get the report"}
              </button>
            </form>
          )}
        </section>

        <footer className="rp-foot small muted">
          Numbers from public Instagram posts. Brandwall is not affiliated with {b.name}, and no
          post has been shown anywhere. Brandwall (Vikba ApS) · brandwall.app
        </footer>
      </div>
    </div>
  );
}
